import React, { Dispatch, KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import Note from '@repo/utils/models/Note';
import TextEditor from '@repo/components/text-editor';
import { KeyboardShortcut } from '@repo/components/tooltips';
import { CheckIcon, ReminderAlarmClockIcon, XIcon } from '@repo/icons';
import styles from './TimelineNoteEditor.module.scss';
import Occasion from '@repo/utils/helpers/Occasion';
import { useOutsideEventHandler } from '@repo/hooks';
import isModifiedEvent from '@repo/utils/helpers/isModifiedEvent';
import { DateTimePicker } from '@repo/components/forms';
import { metaSymbol } from '@repo/utils/helpers/keyboardHandler';
import { SetReminderTooltip } from '@repo/components/tooltips';

type NoteEditorProps = {
  note: Note;
  onNoteContentChange: (content: string) => void;
  onNoteReminderChange: (date: Occasion | null) => void;
  finishEditing: () => void;
  canSave: boolean;
  ignoreOutsideClicks?: boolean;
  onActionClick?: () => void;
  renderWithReminderEditorOpen?: boolean;
  reminderEditorOpen?: boolean;
  setReminderEditorOpen?: Dispatch<boolean>;
};

// move form data up to parent component so we don't keep re-fetching

export function TimelineNoteEditor(props: NoteEditorProps) {
  const [followupOccasion, setFollowupOccasion] = useState(props.note.reminder?.date);
  const [followupEntityInteracted, setFollowupEntityInteracted] = useState(false);
  const showReminderBtn = !props.note.reminder?.date && !props.note.isReadOnly;
  const formRef = useRef(null);

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Escape') {
      props.finishEditing();
    }

    if (isModifiedEvent(e) && e.key === 'Enter') {
      props.finishEditing();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    props.finishEditing();
  };

  const saveBtn = (
    <KeyboardShortcut keys={[metaSymbol, 'enter']}>
      <button className={cn('btn-none', styles.submit)} type="submit">
        <CheckIcon width={15} height={15} strokeWidth="2.2" color="primary" hoverOpacity />
      </button>
    </KeyboardShortcut>
  );

  const dismissBtn = (
    <button onClick={props.finishEditing} className={cn('btn-none', styles.remove)} type="button">
      <XIcon width={15} height={15} color="primary" hoverOpacity />
    </button>
  );

  useOutsideEventHandler(
    formRef,
    () => {
      if (!props.ignoreOutsideClicks) {
        props.finishEditing();
      }
    },
    'click',
    [props.ignoreOutsideClicks],
  );

  useEffect(() => {
    props.onNoteReminderChange(followupOccasion);
    if (!followupOccasion && followupEntityInteracted) {
      setFollowupEntityInteracted(false);
    }
  }, [followupOccasion]);

  return (
    <form ref={formRef} onKeyDown={handleKeyDown} onSubmit={handleFormSubmit}>
      <TextEditor
        className={cn(styles.note, 'timeline-note-editor')}
        content={props.note.content}
        onSave={props.finishEditing}
        onContentChange={props.onNoteContentChange}
        followupOccasion={followupOccasion}
        setFollowupOccasion={setFollowupOccasion}
        setFollowupEntityInteracted={setFollowupEntityInteracted}
        disableFollowupParsing={props.note.hasReminderButNoFollowupEntity}
      />
      <div
        className={cn(styles.actions, showReminderBtn && styles.hasLeftActions)}
        onClick={props.onActionClick}
      >
        {showReminderBtn && (
          <div onClick={(e) => e.preventDefault()} style={{ marginBottom: -1 }}>
            <DateTimePicker
              dateTime={followupOccasion}
              setDateTime={setFollowupOccasion}
              trigger={
                <SetReminderTooltip>
                  <ReminderAlarmClockIcon
                    className={styles.addReminderBtn}
                    width="18"
                    height="18"
                  />
                </SetReminderTooltip>
              }
              defaultIsOpen={!!props.renderWithReminderEditorOpen}
              isOpen={props.reminderEditorOpen}
              setIsOpen={props.setReminderEditorOpen}
            />
          </div>
        )}

        {props.canSave ? saveBtn : dismissBtn}
      </div>
    </form>
  );
}
